import { BuilderTree, type BuilderTreeNode } from "@/features/catalog/authoring";
import { ItemEditor } from "./item-editor";
import { ScoresPanel } from "./scores-panel";
import { type BuilderItemType, type DraftNode, ITEM_TYPES } from "./types";
import type { QuestionnaireBuilderActions, useQuestionnaireBuilder } from "./use-questionnaire-builder";
import { allowedChildTypes } from "./utils/allowed-types";

interface QuestionnaireBuilderProps {
  performance?: boolean;
  /** The builder's own state, owned by the caller so it can read sections at save. */
  builder: Omit<ReturnType<typeof useQuestionnaireBuilder>, "store"> & { actions: QuestionnaireBuilderActions };
}

/**
 * The question tree and its item editor sheet. Title, description and status
 * are not here — the definition shell owns those three, and a builder that
 * also declared them would be a second source of truth for the same values.
 */
export function QuestionnaireBuilder({ builder, performance = false }: QuestionnaireBuilderProps) {
  const { draft, scoringEnabled, selectedItemPath, selectedItem, actions } = builder;

  const nodes = toTreeNodes(draft.sections);
  const selectedId = selectedItem?.clientId ?? null;
  const parentOf = (parentId: string | null) =>
    parentId ? findNode(draft.sections, findPath(draft.sections, parentId) ?? []) : undefined;
  const treeActions = {
    select: (id: string) => actions.selectItem(findPath(draft.sections, id)),
    add: (parentId: string | null, kind?: string) => {
      if (!kind) return;
      actions.addItem(parentId ? (findPath(draft.sections, parentId) ?? []) : [], kind as BuilderItemType);
    },
    move: (parentId: string | null, fromIndex: number, toIndex: number) => {
      const parentPath = parentId ? (findPath(draft.sections, parentId) ?? []) : [];
      actions.moveItem([...parentPath, fromIndex], toIndex);
    },
    remove: (id: string) => {
      const path = findPath(draft.sections, id);
      if (path) actions.removeItem(path);
    },
    duplicate: (id: string) => {
      const path = findPath(draft.sections, id);
      if (path) actions.duplicateItem(path);
    },
  };

  return (
    <>
      <BuilderTree.Provider
        nodes={nodes}
        selectedId={selectedId}
        actions={treeActions}
        collectionLabel="Questions"
        addLabel={(parent) => (parent ? "Item" : "Section")}
        addOptions={(parent) =>
          typeOptions(
            allowedChildTypes(parentOf(parent?.id ?? null)).filter(
              (type) => !performance || !["RepeatingGroup", "Attachment", "Url"].includes(type),
            ),
          )
        }
      >
        <BuilderTree.Header />
        <BuilderTree.Items />
      </BuilderTree.Provider>

      {scoringEnabled && (
        <ScoresPanel
          performance={performance}
          sections={draft.sections}
          scores={draft.scores}
          totalBands={draft.totalBands ?? []}
          onChange={actions.setScores}
          onTotalBandsChange={actions.setTotalBands}
        />
      )}

      <BuilderTree.EditorSheet
        open={!!selectedItem}
        onOpenChange={(open) => {
          if (!open) actions.selectItem(null);
        }}
        title={`Edit ${selectedItem ? editorNoun(selectedItem) : "Item"}`}
        className="sm:!max-w-2xl"
      >
        {selectedItem && selectedItemPath && (
          <ItemEditor
            key={selectedItem.clientId}
            performance={performance}
            scoringEnabled={scoringEnabled}
            item={selectedItem}
            path={selectedItemPath}
            allItems={draft.sections}
            actions={actions}
          />
        )}
      </BuilderTree.EditorSheet>
    </>
  );
}

function typeOptions(types: BuilderItemType[]) {
  return ITEM_TYPES.filter((type) => types.includes(type.value)).map(({ label, value }) => ({ label, value }));
}

function editorNoun(item: DraftNode): string {
  if (item.kind === "RepeatingGroup") return "Repeating Group";
  return item.kind;
}

function toTreeNodes(items: DraftNode[]): BuilderTreeNode[] {
  return items.map((item) => {
    const isContainer = item.kind === "Section" || item.kind === "RepeatingGroup";
    const type = item.kind === "Question" ? item.value.kind : item.kind;
    const kindLabel = ITEM_TYPES.find((candidate) => candidate.value === type)?.label ?? type;
    const badges = [
      ...(item.kind === "Question" && item.required ? ["Required"] : []),
      ...(item.kind === "RepeatingGroup" ? ["Repeats"] : []),
      ...("conditions" in item && item.conditions?.length
        ? [`${item.conditions.length} condition${item.conditions.length > 1 ? "s" : ""}`]
        : []),
    ];
    return {
      id: item.clientId,
      title: item.kind === "Display" ? item.text : item.label,
      kindLabel,
      badges,
      isContainer,
      ...(isContainer ? { children: toTreeNodes(item.items) } : {}),
    };
  });
}

function findPath(items: DraftNode[], id: string, parent: number[] = []): number[] | null {
  for (const [index, item] of items.entries()) {
    const path = [...parent, index];
    if (item.clientId === id) return path;
    const child = "items" in item ? findPath(item.items, id, path) : null;
    if (child) return child;
  }
  return null;
}

function findNode(nodes: DraftNode[], path: number[]): DraftNode | undefined {
  const [head, ...rest] = path;
  if (head === undefined) return undefined;
  const node = nodes[head];
  if (!node || !rest.length) return node;
  return "items" in node ? findNode(node.items, rest) : undefined;
}
